
"use client";
import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import { Menu as MenuIcon } from "@mui/icons-material";
import {
    AdminPanelSettings, Business, ReceiptLong, PersonAdd, GroupWork, SettingsApplications,
    Group as GroupIcon, GroupAdd as GroupAddIcon, Pending as PendingIcon, TaskAlt,
    CurrencyExchange as CurrencyExchangeIcon, Description, AccountBalanceWallet, HighlightOff,
    Assessment, Summarize, RequestQuote, Payments, FactCheck
} from "@mui/icons-material";

// Base Menu Data
const baseMenuData = [
    {
        id: "Admin",
        title: "Administration",
        icon: <AdminPanelSettings fontSize="small" />,
        subMenu: [
            { label: "Create User", path: "/pages/addUser", icon: <PersonAdd fontSize="small" /> },
            { label: "Vendor Master", path: "/pages/vendorMaster", icon: <GroupIcon fontSize="small" /> },
            { label: "Vendor Grouping", path: "/pages/vendorGrouping", icon: <GroupAddIcon fontSize="small" /> },
            { label: "Report Configuration", path: "/pages/reportConfig", icon: <SettingsApplications fontSize="small" /> },
        ],
    },
    {
        id: "transactions",
        title: "Transactions",
        icon: <ReceiptLong fontSize="small" />,
        subMenu: [
            { label: "Payment Approve", path: "/pages/paymentApprove", icon: <PendingIcon fontSize="small" /> },
            { label: "Approved Payment", path: "/pages/approvedPayment", icon: <TaskAlt fontSize="small" /> },
            { label: "Process Payment", path: "/pages/processPayment", icon: <CurrencyExchangeIcon fontSize="small" /> },
            { label: "Payment Request", path: "/pages/paymentRequest", icon: <RequestQuote fontSize="small" /> },
            { label: "Advance Payment Request", path: "/pages/advancePaymentRequest", icon: <Payments fontSize="small" /> },
            { label: "Request Status", path: "/pages/requestStatus", icon: <FactCheck fontSize="small" /> },
            { label: "Payment Details", path: "/pages/paymentDetails", icon: <Description fontSize="small" /> },
            { label: "View Due Statement", path: "/pages/viewStatement", icon: <AccountBalanceWallet fontSize="small" /> },
            { label: "Rejected Details", path: "/pages/rejectedDetails", icon: <HighlightOff fontSize="small" /> },
            { label: "SAP Payment Report", path: "/pages/sapPaymentReport", icon: <Assessment fontSize="small" /> },
            { label: "Reports", path: "/pages/reports", icon: <Summarize fontSize="small" /> },

        ],
    },
];

// const getFilteredMenuData = () => {
export const getFilteredMenuData = (userType: string) => {
    if (!userType) return [];

    switch (userType) {
        case "Admin":
            return baseMenuData.filter((menu) => menu.id === "Admin");

        case "Approver":
            return baseMenuData
                .filter((menu) => menu.id === "transactions")
                .map((menu) => ({
                    ...menu,
                    subMenu: menu.subMenu.filter((item) =>
                        [
                            "Payment Approve", "Approved Payment", "Process Payment",
                            "Payment Details", "View Due Statement", "Rejected Details",
                            "SAP Payment Report", "Reports",
                        ].includes(item.label),
                    ),
                }));

        case "Processer":
            return baseMenuData
                .filter((menu) => menu.id === "transactions")
                .map((menu) => ({
                    ...menu,
                    subMenu: menu.subMenu.filter((item) =>
                        [
                            "Process Payment", "Payment Details", "View Due Statement",
                            "Rejected Details", "SAP Payment Report", "Reports",
                        ].includes(item.label),
                    ),
                }));
        case "Processor":
            return baseMenuData
                .filter((menu) => menu.id === "transactions")
                .map((menu) => ({
                    ...menu,
                    subMenu: menu.subMenu.filter((item) =>
                        [
                            "Process Payment", "Payment Details", "View Due Statement",
                            "Rejected Details", "SAP Payment Report", "Reports",
                        ].includes(item.label),
                    ),
                }));

        case "Requester":
            return baseMenuData
                .filter((menu) => menu.id === "transactions")
                .map((menu) => ({
                    ...menu,
                    subMenu: menu.subMenu.filter((item) =>
                        [
                            "Payment Request", "Advance Payment Request", "Request Status",
                            "Payment Details", "Rejected Details", "Reports",
                        ].includes(item.label),
                    ),
                }));

        default:
            return [];
    }
};

export default function SidebarLayout() {
    const [selectedMenu, setSelectedMenu] = useState<string | null>(null);
    const [isCollapsed, setIsCollapsed] = useState<boolean>(false);
    const [userType, setUserType] = useState<string | null>(null);
    const router = useRouter();
    const themeColor = typeof window !== "undefined" ? localStorage.getItem("themeColor") : null;
    useEffect(() => {
        const type = localStorage.getItem("userType");
        const storedMenu = localStorage.getItem("selectedMenu");
        const collapsed = localStorage.getItem("sidebarCollapsed");

        if (type) setUserType(type);
        if (storedMenu) setSelectedMenu(storedMenu);
        if (collapsed === "true") setIsCollapsed(true);
    }, []);

    useEffect(() => {
        localStorage.setItem("sidebarCollapsed", isCollapsed.toString());
    }, [isCollapsed]);

    const handleMenuClick = (hasSubMenu: boolean, path?: string) => {
        if (!hasSubMenu && path) {
            router.push(path);
        }
    };



    if (!userType) return null;

    return (
        <div className="flex min-h-screen">
            {/* Sidebar */}
            <aside
                style={{ background: themeColor || "red"}}
                className={`text-white flex flex-col p-4 shadow-md overflow-y-auto transition-all duration-300 ${isCollapsed ? "w-20" : "w-64"}`}
            >
                {/* Toggle Button */}
                <button
                    onClick={() => setIsCollapsed(!isCollapsed)}
                    className={`mb-4 text-white hover:text-gray-300 ${isCollapsed ? "self-start" : "self-end"}`}
                >
                    <MenuIcon />
                </button>

                {/* Navigation */}
                <nav className="space-y-2">
                    {getFilteredMenuData(userType).map((menu) => {
                        const hasSubMenu = Array.isArray(menu.subMenu) && menu.subMenu.length > 0;

                        return (
                            <div key={menu.id}>
                                <button
                                    onClick={() => handleMenuClick(hasSubMenu, menu.subMenu[0]?.path)}
                                    className="flex items-center w-full px-3 py-2 rounded-lg hover:bg-gray-700 text-left"
                                    title={isCollapsed ? menu.title : ""}
                                >
                                    <span className="flex items-center gap-3">
                                        {menu.icon}
                                        {!isCollapsed && <span>{menu.title}</span>}
                                    </span>
                                </button>

                                {/* Submenu */}
                                {hasSubMenu && (
                                    <ul className={`mt-1 space-y-1 ${isCollapsed ? "pl-2" : "pl-8"}`}>
                                        {menu.subMenu.map((sub, idx) => (
                                            <li key={idx}>
                                                <button
                                                    onClick={() => router.push(sub.path)}
                                                    className="flex items-center w-full px-3 py-2 rounded-md hover:bg-gray-700 text-sm text-white text-left"
                                                    title={isCollapsed ? sub.label : ""}
                                                >
                                                    {sub.icon}
                                                    {!isCollapsed && <span className="ml-2">{sub.label}</span>}
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        );
                    })}
                </nav>
            </aside>





        </div>
    );
}

